import React, { useCallback, useMemo, useState } from 'react';
import { Dropdown, Spinner } from 'react-bootstrap';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { FaDownload, FaFileExcel, FaFilePdf } from 'react-icons/fa';
import FileSaver from 'file-saver';
import ExcelJS from 'exceljs';
import { generatePdfReport } from '../utils/pdfGenerator';
import dateFormat from "dateformat";
import {
  buildMeasureAveragesRow,
  buildMeasureExportSheet,
} from "../../../utils/measureFields";

const PdfDownloader = ({
  user,
  empresa,
  serialnumber,
  data,
  limits,
  stats,
  mean,
  weight,
  weightStats,
  curvaSelecionada,
  currency = 'BRL',
  sacaPrice,
  permissionSGU,
  buttonLabel = 'Baixar Relatório',
  toggleId = 'dash-medidas-download-toggle',
}) => {
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [isLoadingExcel, setIsLoadingExcel] = useState(false);

  // Estatísticas ajustadas para o PDF: percentuais relativos apenas às fatias exibidas (exclui 'Rodízio')
  const pdfStats = useMemo(() => {
    const exact = stats?.exact || 0;
    const below = stats?.below || 0;
    const above = stats?.above || 0;
    const totalSlices = exact + below + above;
    const safeTotal = totalSlices > 0 ? totalSlices : 1; // evita divisão por zero
    return {
      exact,
      below,
      above,
      getPercentage: (value) => ((value / safeTotal) * 100).toFixed(1),
      totalSlices
    };
  }, [stats]);

  // Função para obter datas das medições
  const getMeasurementDates = useCallback((measurements) => {
    if (!measurements || measurements.length === 0) return { first: null, last: null };

    const sortedData = [...measurements].sort((a, b) => new Date(a.date) - new Date(b.date));
    return {
      first: sortedData[0].date,
      last: sortedData[sortedData.length - 1].date
    };
  }, []);

  // Função para gerar o gráfico de estatísticas
  const brandColors = useMemo(() => ({
    lightGreen: '#8fad15',
    darkGreen: '#1c4220',
    success: 'rgba(143, 173, 21, 0.8)',
    error: 'rgba(220, 53, 69, 0.8)',
    warning: 'rgba(255, 193, 7, 0.8)'
  }), []);

  const generateStatsChart = useCallback(async () => {
    const { default: Chart } = await import('chart.js/auto');
    Chart.register(ChartDataLabels); // Registrar plugin

    const canvas = document.createElement('canvas');
    canvas.width = 400; // Reduzido para melhor proporção do gráfico de pizza
    canvas.height = 400;

    const ctx = canvas.getContext('2d');

    // Percentuais devem considerar apenas as fatias exibidas (exclui 'Rodízio')
    const totalSlices = pdfStats.totalSlices || 0;
    const getSlicePercent = (value) => {
      if (!totalSlices) return '0.0';
      return ((value / totalSlices) * 100).toFixed(1);
    };

  new Chart(ctx, {
      type: 'pie', // Mudado de doughnut para pie
      data: {
        labels: ['Dentro do Limite', 'Abaixo do Limite', 'Acima do Limite'],
        datasets: [{
          data: [pdfStats.exact, pdfStats.below, pdfStats.above],
          backgroundColor: [
            brandColors.success,
            brandColors.error,
            brandColors.warning
          ],
          borderColor: [
            brandColors.lightGreen,
            '#dc3545',
            '#ffc107'
          ],
          borderWidth: 1 // Reduzido para melhor aparência
        }]
      },
      options: {
        responsive: false,
        animation: false, // Desabilitar animação
        plugins: {
          title: {
            display: true,
            // Usar somatório das fatias para manter coerência com o gráfico
            text: ['Distribuição das Medidas', `Total: ${totalSlices} medições`],
            color: brandColors.darkGreen,
            font: {
              size: 16,
              weight: 'bold'
            },
            padding: 20
          },
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              boxWidth: 15,
              padding: 15,
              font: {
                size: 12
              },
              generateLabels: (chart) => {
                const chartData = chart.data;
                if (chartData.labels.length && chartData.datasets.length) {
                  return chartData.labels.map((label, i) => {
                    const value = chartData.datasets[0].data[i];
                    const percentage = getSlicePercent(value);
                    return {
                      text: `${label}: ${value} (${percentage}%)`,
                      fillStyle: chartData.datasets[0].backgroundColor[i],
                      strokeStyle: chartData.datasets[0].borderColor[i],
                      lineWidth: 1,
                      hidden: false,
                      index: i
                    };
                  });
                }
                return [];
              }
            }
          },
          datalabels: {
            color: '#fff',
            font: {
              weight: 'bold',
              size: 14
            },
            formatter: (value) => {
              const percentage = parseFloat(getSlicePercent(value));
              return percentage > 5 ? `${percentage}%` : '';
            },
            padding: 6
          }
        }
      }
    });

    // Aguardar o gráfico ser renderizado
    await new Promise(resolve => setTimeout(resolve, 100));

    return canvas;
  }, [pdfStats, brandColors]);

  // Função para gerar o gráfico de peso
  const generateWeightChart = useCallback(async () => {
    const { default: Chart } = await import('chart.js/auto');
    Chart.register(ChartDataLabels); // Registrar plugin

    const canvas = document.createElement('canvas');
    canvas.width = 400;
    canvas.height = 400;

    const ctx = canvas.getContext('2d');

  new Chart(ctx, {
      type: 'pie', // Mudado de doughnut para pie
      data: {
        labels: ['Peso Padrão', 'Peso Desconto'],
        datasets: [{
          data: [weightStats.inRange, weightStats.outRange],
          backgroundColor: [
            'rgba(25, 135, 84, 0.8)',
            'rgba(220, 53, 69, 0.8)'
          ],
          borderColor: [
            'rgb(25, 135, 84)',
            'rgb(220, 53, 69)'
          ],
          borderWidth: 1 // Reduzido para melhor aparência
        }]
      },
      options: {
        responsive: false,
        animation: false,
        plugins: {
          title: {
            display: true,
            text: 'Distribuição do Peso',
            font: { size: 16, weight: 'bold' },
            padding: 20
          },
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              boxWidth: 15,
              padding: 15,
              font: { size: 12 },
              generateLabels: (chart) => {
                const chartData = chart.data;
                return chartData.labels.map((label, i) => ({
                  text: `${label}: ${chartData.datasets[0].data[i].toFixed(1)} ton`,
                  fillStyle: chartData.datasets[0].backgroundColor[i],
                  strokeStyle: chartData.datasets[0].borderColor[i],
                  lineWidth: 1,
                  hidden: false,
                  index: i
                }));
              }
            }
          },
          datalabels: {
            color: '#fff',
            font: { weight: 'bold', size: 14 },
            formatter: (value) => `${((value / weight) * 100).toFixed(1)}%`,
            padding: 6
          }
        }
      }
    });

    // Aguardar o gráfico ser renderizado
    await new Promise(resolve => setTimeout(resolve, 100));

    return canvas;
  }, [weightStats, weight]);

  const calculateStatistics = useCallback((dataset) => {
    if (!dataset || dataset.length === 0) return null;

    const humidityValues = dataset.map(item => parseFloat(item.humidity));
    const sorted = [...humidityValues].sort((a, b) => a - b);

    return {
      min: Math.min(...humidityValues).toFixed(1),
      max: Math.max(...humidityValues).toFixed(1),
      median: sorted[Math.floor(sorted.length / 2)].toFixed(1),
      q1: sorted[Math.floor(sorted.length / 4)].toFixed(1),
      q3: sorted[Math.floor((3 * sorted.length) / 4)].toFixed(1),
      variance: (humidityValues.reduce((acc, val) => acc + Math.pow(val - mean.exactMean, 2), 0) / humidityValues.length).toFixed(2),
      stdDev: Math.sqrt(humidityValues.reduce((acc, val) => acc + Math.pow(val - mean.exactMean, 2), 0) / humidityValues.length).toFixed(2)
    };
  }, [mean.exactMean]);

  // Adicionar função calculateTimeStats
  const calculateTimeStats = useCallback((measurements) => {
    if (!measurements || measurements.length === 0) {
      return { days: 0, hours: 0, avgMeasuresPerHour: 0 };
    }

    try {
      const sortedData = [...measurements].sort((a, b) => new Date(a.date) - new Date(b.date));
      const firstDate = new Date(sortedData[0].date);
      const lastDate = new Date(sortedData[sortedData.length - 1].date);

      const diffMilliseconds = Math.abs(lastDate - firstDate);
      const diffHours = diffMilliseconds / (1000 * 60 * 60);
      const days = Math.floor(diffHours / 24);
      const hours = Math.floor(diffHours % 24);
      const avgMeasuresPerHour = (measurements.length / diffHours).toFixed(1);

      return {
        days,
        hours,
        avgMeasuresPerHour
      };
    } catch (error) {
      console.error('Erro no cálculo de tempo:', error);
      return { days: 0, hours: 0, avgMeasuresPerHour: 0 };
    }
  }, []);

  const handleDownloadPdf = useCallback(async () => {
    setIsLoadingPdf(true);

    try {
      const { pdf, dateStr, empresa: empresaName } = await generatePdfReport({
        user,
        empresa,
        serialnumber,
        data,
        limits,
        // usar estatísticas ajustadas para o PDF
        stats: pdfStats,
        mean,
        weight,
        weightStats,
        curvaSelecionada,
        generateStatsChart,
        generateWeightChart,
        getMeasurementDates,
        calculateStatistics,
        calculateTimeStats,
        dateFormat,
        currency,
        sacaPrice
      });

      // Salvar o PDF gerado
      pdf.save(`relatorio-${user?.hideSensitiveInfo ? '' : empresaName}-${dateStr.replace(/\//g, '-')}.pdf`);
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      alert('Ocorreu um erro ao gerar o PDF. Por favor, tente novamente.');
    } finally {
      setIsLoadingPdf(false);
    }
  }, [user, curvaSelecionada, empresa, serialnumber, data, limits, pdfStats, mean, weight, weightStats, currency, sacaPrice,
    generateStatsChart, generateWeightChart, getMeasurementDates, calculateStatistics,
    calculateTimeStats]);

  const handleDownloadExcel = useCallback(async () => {
    if (!data?.length || isLoadingExcel) return;

    setIsLoadingExcel(true);
    try {
      const units =
        JSON.parse(localStorage.getItem("units")) ||
        [JSON.parse(localStorage.getItem("cooperative"))] ||
        [];

      const handleGetUnit = (unitId) => {
        const result = units.find((item) => item._id === unitId);
        return result?.name || "--";
      };

      const { headers, mappedRows } = buildMeasureExportSheet({
        dataList: data,
        user,
        empresa,
        serialnumber,
        handleGetUnit,
        dateFormat,
      });

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Data");
      worksheet.addRow(headers);

      const headerRow = worksheet.getRow(1);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, size: 14, color: { argb: "FFFFFFFF" } };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF0C392E" },
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
      });
      headerRow.height = 30;

      headers.forEach((header, index) => {
        worksheet.getColumn(index + 1).width = header.length + 20;
      });

      mappedRows.forEach((rowData, rowIndex) => {
        const row = worksheet.addRow(Object.values(rowData));
        row.eachCell((cell) => {
          cell.alignment = { vertical: "middle", horizontal: "center" };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: rowIndex % 2 === 0 ? "FFF4F4F4" : "FFCAEB8C" },
          };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        });
      });

      worksheet.addRow([]);

      const averagesRow = worksheet.addRow(
        buildMeasureAveragesRow(headers, data)
      );

      averagesRow.eachCell((cell) => {
        cell.font = { bold: true, size: 12 };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFE8F5E9" },
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
        cell.border = {
          top: { style: "medium" },
          left: { style: "thin" },
          bottom: { style: "medium" },
          right: { style: "thin" },
        };
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const fileName = user?.hideSensitiveInfo
        ? "Relatorio"
        : empresa || "Relatorio";
      FileSaver.saveAs(
        blob,
        `${fileName}_${new Date().toISOString().slice(0, 10)}.xlsx`
      );
    } catch (error) {
      console.error("Erro ao gerar Excel:", error);
      alert("Ocorreu um erro ao gerar o Excel. Por favor, tente novamente.");
    } finally {
      setIsLoadingExcel(false);
    }
  }, [data, empresa, isLoadingExcel, serialnumber, user]);

  const isBusy = isLoadingPdf || isLoadingExcel;
  const canWrite = permissionSGU?.home?.write !== false;
  const hasData = Array.isArray(data) && data.length > 0;

  return (
    <Dropdown align="end">
      <Dropdown.Toggle
        variant="success"
        size="sm"
        className={`dash-btn ${!hasData ? "dash-btn--muted" : ""}`}
        disabled={isBusy || !canWrite}
        id={toggleId}
      >
        {isBusy ? (
          <>
            <Spinner animation="border" size="sm" />{" "}
            <span>
              {isLoadingPdf ? "Gerando PDF..." : "Gerando Excel..."}
            </span>
          </>
        ) : (
          <>
            <FaDownload />
            <span> {buttonLabel}</span>
          </>
        )}
      </Dropdown.Toggle>

      <Dropdown.Menu popperConfig={{ strategy: "fixed" }}>
        <Dropdown.Item
          onClick={handleDownloadExcel}
          disabled={isBusy || !hasData || !canWrite}
        >
          <FaFileExcel className="me-2 text-success" />
          Excel (.xlsx)
        </Dropdown.Item>
        <Dropdown.Item
          onClick={handleDownloadPdf}
          disabled={isBusy || !hasData || !canWrite}
        >
          <FaFilePdf className="me-2 text-danger" />
          PDF
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default React.memo(PdfDownloader);
